import { concave } from '@turf/concave';
import { featureCollection, point } from '@turf/helpers';

// turf's `concave` filters triangle edges by GEOGRAPHIC distance (@turf/distance),
// so it only behaves sensibly for lng/lat inputs. Our points are image-pixel
// coordinates (e.g. [120, 240]) — interpreted as lng/lat, adjacent pixels are
// ~100 km apart, so every edge exceeds `maxEdge` and `concave` returns null.
//
// To keep the concave-hull behaviour in planar pixel space we scale the points
// into a tiny window near [0, 0] (a valid, effectively planar lat/lng range) and
// express `concavity` (a pixel length) in the matching 'degrees' unit, then scale
// the resulting polygon back to pixel space. Near the origin, turf's great-circle
// distance in degrees ≈ the Euclidean pixel distance times this scale, so the
// edge filter behaves like a planar `maxEdge` of `concavity` pixels.
const GEO_SCALE = 1e-4;

export function hull(
  points: [number, number][],
  concavity: number,
): [number, number][] {
  if (!points || points.length < 3) return [];

  const fc = featureCollection(
    points.map(p => point([p[0] * GEO_SCALE, p[1] * GEO_SCALE])),
  );

  const concaveHull = concave(fc, {
    maxEdge: concavity * GEO_SCALE,
    units: 'degrees',
  });

  if (!concaveHull || concaveHull.geometry.type !== 'Polygon') {
    return [];
  }

  return (concaveHull.geometry.coordinates[0] as [number, number][]).map(
    ([x, y]) => [x / GEO_SCALE, y / GEO_SCALE] as [number, number],
  );
}
